import React, {ReactElement, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {useSaveAppliedRulesMutation} from '../../../../hooks/useSaveAppliedRules';
import {selectedPresentationSelector} from '../../../../store/selectors/presentation.selectors';
import {ISlide} from '../../../../incase-domain/slide';
import {MultiSelect} from '../../../../ui/components/common/MultiSelect';
import {RuleOperator, RuleResultType} from '../../../../incase-domain/appliedRules';
import {IElementQuestion} from '../../../../incase-domain/elements/question/question';
import {IElementQuestionYesNo} from '../../../../incase-domain/elements/question/questionYesNo';
import {IElementPhotoAnswerQuestion} from '../../../../incase-domain/elements/question/photoAnswerQuestion';
import {IElementMultiSelect} from '../../../../incase-domain/elements/question/multiSelect';
import {selectedSlideSelector} from '../../../../store/selectors/slide.selectors';
import TooltipWrapper from '../../../../ui/components/common/TooltipWrapper';
import {isRuleInFocus} from '../../../../utils/rule';
import {IElementQuestionAnswer} from '../../../../incase-domain/elements/base/questionElementBase';

interface AppliedRuleSlideSelectorProps {
    element: IElementQuestion | IElementQuestionYesNo | IElementPhotoAnswerQuestion | IElementMultiSelect;
    answerIndex: number;
    codebookAnswer?: IElementQuestionAnswer;
}

function AppliedRuleSlideSelector({element, answerIndex, codebookAnswer}: AppliedRuleSlideSelectorProps): ReactElement {
    const {t} = useTranslation();
    const {mutate: saveAppliedRules} = useSaveAppliedRulesMutation();

    const presentation = useSelector(selectedPresentationSelector);
    const {appliedRules = [], slides, slideIndexList} = presentation;
    const currentSlide = useSelector(selectedSlideSelector);

    const [selectedSlides, setSelectedSlides] = useState<{label: string; value: string}[]>([]);

    const answerIndexOrId = codebookAnswer?.codebookEntryId || answerIndex;

    const slideIdsAfterCurrentSlide = slideIndexList.slice(slideIndexList.indexOf(currentSlide._id) + 1);
    const slidesAfterCurrentSlide = slides.filter((s) => slideIdsAfterCurrentSlide.includes(s._id));
    const slideOptions = slidesAfterCurrentSlide.map((s) => mapToOptions(s));

    function mapToOptions(item: ISlide | {_id: string; name: string}): {label: string; value: string} {
        return {label: item.name, value: item._id};
    }

    const appliedRule = appliedRules?.find((r) => isRuleInFocus(r, element.id, answerIndexOrId));

    useEffect(() => {
        if (appliedRule) {
            const slidesShownForPredicate: ISlide[] = slides.filter((s) => appliedRule.results[0].slideIds.includes(s._id));
            setSelectedSlides(slidesShownForPredicate.map((s) => mapToOptions(s)));
        }
    }, [appliedRule, slides]);

    const handleSaveRule = (): void => {
        const slideIds = selectedSlides.map((s) => s.value);
        let updatedRules;
        if (!slideIds.length) {
            // delete rule
            updatedRules = appliedRules.filter((r) => !isRuleInFocus(r, element.id, answerIndexOrId));
        } else if (appliedRule) {
            // update rule
            updatedRules = appliedRules.map((r) => {
                if (isRuleInFocus(r, element.id, answerIndexOrId)) {
                    const updatedRule = {...r};
                    updatedRule.results[0].slideIds = slideIds;
                    return updatedRule;
                }
                return r;
            });
        } else {
            // add rule
            updatedRules = [
                ...appliedRules,
                {
                    predicates: [
                        {
                            elementType: element.type,
                            elementId: element.id,
                            answers: [answerIndexOrId],
                            operator: RuleOperator.IF,
                        },
                    ],
                    results: [
                        {
                            resultType: RuleResultType.SHOW_SLIDES,
                            slideIds,
                        },
                    ],
                    name: `${element.name} - ${typeof answerIndexOrId === 'number' ? answerIndexOrId + 1 : codebookAnswer?.text}`,
                },
            ];
        }

        saveAppliedRules({appliedRules: updatedRules, presentationId: presentation._id});
    };

    return (
        <TooltipWrapper disable={!!element.id} id="presentation-newSlide" content={t('designSidepanel.saveSlideBefore')}>
            <MultiSelect
                value={selectedSlides}
                options={slideOptions}
                onChange={(options) => options && setSelectedSlides(options)}
                onBlur={handleSaveRule}
                disabled={!element.id}
                menuPositionOverridden
            />
        </TooltipWrapper>
    );
}

export default AppliedRuleSlideSelector;
